import { useMemo, useState } from 'react';
import { GEWERK } from './gemeinsam';
import {
  ContentSection, DataTable, EmptyState, Feld, LoadingState, StatusBadge,
  type Spalte, type Zustand,
} from '../../muster';

/**
 * Planeraufgaben — ein Cluster ist eine Entscheidung. Kollisionen im vollen
 * Deckenhohlraum, wo kein Gewerk mehr ausweichen kann.
 */
export function Planeraufgaben({
  planeraufgaben, status, onStatusChange, offenId, onToggle, detail,
  entscheidungstext, onEntscheidungstextChange, onEntscheiden,
  onErlaeutern, erlaeuternLaeuft,
}: {
  planeraufgaben: any;
  status: string;
  onStatusChange: (status: string) => void;
  offenId: string | null;
  onToggle: (id: string) => void;
  detail: any;
  entscheidungstext: string;
  onEntscheidungstextChange: (text: string) => void;
  onEntscheiden: (status: string) => void;
  onErlaeutern: () => void;
  erlaeuternLaeuft: boolean;
}) {
  // Batch 8: KiModell steht als "{anbieter}:{modell}" in der Datenbank
  // (kein Netzaufruf ist "der eine" Anbieter mehr) -- fuer die Anzeige in
  // "{anbieter} · {modell}" umgeformt.
  const anbieterModell = (kiModell: string | null | undefined) => {
    if (!kiModell) return '—';
    const trennstelle = kiModell.indexOf(':');
    if (trennstelle < 0) return kiModell;
    return `${kiModell.slice(0, trennstelle)} · ${kiModell.slice(trennstelle + 1)}`;
  };

  // ════════════════════════════════════════════════════════════════════
  // BATCH 7b — GESCHOSS/ABSCHNITT/GEWERK UND SORTIERUNG, REIN CLIENTSEITIG
  // ════════════════════════════════════════════════════════════════════
  //
  // In Batch 7 offen gelassen. 'Status' bleibt serverseitig (bestehend,
  // holt die Seite neu) — die drei neuen Filter und die Sortierung
  // arbeiten über die schon geladene Antwort, ohne einen weiteren Aufruf:
  // Ein Planer, der zwischen Geschossen wechselt, braucht keine neue
  // Ladezeit für eine Liste, die längst im Speicher steht.
  const [geschossFilter, setGeschossFilter] = useState('');
  const [abschnittFilter, setAbschnittFilter] = useState('');
  const [gewerkFilter, setGewerkFilter] = useState('');
  const [sortierung, setSortierung] = useState<'kollisionen' | 'hoehe'>(
    'kollisionen');

  const alle: any[] = Array.isArray(planeraufgaben) ? planeraufgaben : [];

  // Die Auswahlwerte kommen aus den Daten, nicht aus einer Liste im Code
  // (dasselbe Prinzip wie bei den Vorschlagsfiltern) — ein Geschoss, das es
  // nicht gibt, taucht hier nicht auf.
  const geschosse = useMemo(
    () => Array.from(new Set(alle.map((p) => p.geschoss).filter(Boolean)))
      .sort((a, b) => a.localeCompare(b)),
    [alle]);
  const abschnitte = useMemo(
    () => Array.from(
      new Set(alle.map((p) => p.planungsbereich).filter(Boolean)))
      .sort((a, b) => a.localeCompare(b)),
    [alle]);
  const gewerke = useMemo(
    () => Array.from(new Set(
      alle.flatMap((p) => String(p.gewerke ?? '').split(','))
        .map((g) => g.trim()).filter(Boolean)))
      .sort((a, b) => a.localeCompare(b)),
    [alle]);

  const gefiltert = alle
    .filter((p) => !geschossFilter || p.geschoss === geschossFilter)
    .filter((p) => !abschnittFilter || p.planungsbereich === abschnittFilter)
    .filter((p) => !gewerkFilter
      || String(p.gewerke ?? '').split(',').map((g) => g.trim())
        .includes(gewerkFilter))
    .sort((a, b) => sortierung === 'hoehe'
      ? (b.fehlendeHoeheMaxMm ?? -1) - (a.fehlendeHoeheMaxMm ?? -1)
      : (b.kollisionen ?? 0) - (a.kollisionen ?? 0));

  const spalten: Spalte<any>[] = [
    {
      schluessel: 'geschoss', titel: 'Geschoss', immerSichtbar: true, breite: '120px',
      wert: (p) => p.geschoss ?? '',
      inhalt: (p) => p.geschoss ?? '—',
    },
    {
      schluessel: 'bereich', titel: 'Bereich', breite: '130px',
      wert: (p) => p.planungsbereich ?? '',
      inhalt: (p) => p.planungsbereich ?? '—',
    },
    {
      schluessel: 'gewerke', titel: 'Gewerke',
      wert: (p) => (p.gewerke ?? '').split(',')
        .map((g: string) => GEWERK[g] ?? g).join(', '),
      inhalt: (p) => (
        <span className="schwach" style={{ fontSize: 11 }}>
          {(p.gewerke ?? '').split(',').map((g: string) => GEWERK[g] ?? g).join(', ')}
        </span>
      ),
    },
    {
      schluessel: 'kollisionen', titel: 'Kollisionen', art: 'zahl', breite: '130px',
      wert: (p) => p.kollisionen,
    },
    {
      schluessel: 'fehlt', titel: 'fehlt bis', art: 'zahl', einheit: 'mm',
      nachkomma: 0, breite: '130px',
      wert: (p) => p.fehlendeHoeheMaxMm,
    },
    {
      schluessel: 'status', titel: 'Status', art: 'status', breite: '150px',
      wert: (p) => p.status,
      inhalt: (p) => (
        <StatusBadge zustand={statuszustand(p.status)} text={p.status} klein />
      ),
    },
  ];

  const offen = gefiltert.find((p) => p.id === offenId) ?? null;

  return (
    <ContentSection
      titel="Planeraufgaben"
      beschreibung={'Kollisionen im vollen Deckenhohlraum, wo kein Gewerk mehr '
        + 'ausweichen kann. Ein Cluster ist eine Entscheidung — Abhangdecke tiefer, '
        + 'Trasse umlegen, Unterzug durchbrechen.'}
      nackt
    >
      <DataTable
        name="Planeraufgaben"
        spalten={spalten}
        zeilen={gefiltert}
        schluessel={(p) => p.id}
        trefferText={`${gefiltert.length} von ${alle.length}`}
        beiZeileKlick={(p) => onToggle(p.id)}
        aktiveZeile={offenId ?? undefined}
        ausfuhr
        ausfuhrDateiname="Planeraufgaben"
        werkzeuge={(
          <>
            <Feld
              art="auswahl" beschriftung="Status"
              wert={status} beiAenderung={(w) => onStatusChange(w as string)}
              optionen={[
                { wert: '', beschriftung: 'alle Status' },
                { wert: 'offen', beschriftung: 'offen' },
                { wert: 'in_arbeit', beschriftung: 'in Arbeit' },
                { wert: 'entschieden', beschriftung: 'entschieden' },
                { wert: 'verworfen', beschriftung: 'verworfen' },
              ]}
            />
            <Feld
              art="auswahl" beschriftung="Geschoss"
              wert={geschossFilter} beiAenderung={(w) => setGeschossFilter(w as string)}
              optionen={[
                { wert: '', beschriftung: 'alle Geschosse' },
                ...geschosse.map((g) => ({ wert: g, beschriftung: g })),
              ]}
            />
            <Feld
              art="auswahl" beschriftung="Abschnitt"
              wert={abschnittFilter} beiAenderung={(w) => setAbschnittFilter(w as string)}
              optionen={[
                { wert: '', beschriftung: 'alle Abschnitte' },
                ...abschnitte.map((a2) => ({ wert: a2, beschriftung: a2 })),
              ]}
            />
            <Feld
              art="auswahl" beschriftung="Gewerk"
              wert={gewerkFilter} beiAenderung={(w) => setGewerkFilter(w as string)}
              optionen={[
                { wert: '', beschriftung: 'alle Gewerke' },
                ...gewerke.map((g) => ({ wert: g, beschriftung: GEWERK[g] ?? g })),
              ]}
            />
            <Feld
              art="auswahl" beschriftung="Sortierung"
              wert={sortierung}
              beiAenderung={(w) => setSortierung(w as 'kollisionen' | 'hoehe')}
              optionen={[
                { wert: 'kollisionen', beschriftung: 'nach Kollisionen ↓' },
                { wert: 'hoehe', beschriftung: 'nach fehlender Höhe ↓' },
              ]}
            />
          </>
        )}
        leer={(
          <EmptyState
            titel="Keine Planeraufgabe in dieser Auswahl."
            erklaerung={'Entweder ist hier alles entschieden, oder die Filter sind zu '
              + 'eng gesetzt.'}
          />
        )}
      />

      {/* Die Einzelheiten stehen UNTER der Tabelle und nicht mehr als
          eingeschobene Zeile darin: `DataTable` virtualisiert über eine feste
          Zeilenhöhe, und eine Zeile, die sich aufklappt, hat keine. Die
          gewählte Zeile ist über `aktiveZeile` hervorgehoben. */}
      {offen && (
        <div className="karte">
          {!detail ? (
            <LoadingState text="Planeraufgabe wird geladen …" zeilen={4} />
          ) : (
            <div style={{ padding: '8px 4px' }}>
                        <div style={{ fontWeight: 600, marginBottom: 6 }}>
                          {detail.titel}
                        </div>
                        <div style={{ fontSize: 12, marginBottom: 8 }}>
                          {detail.beschreibung}
                        </div>

                        {Array.isArray(detail.optionen)
                          && detail.optionen.length > 0 && (
                          <ul style={{ fontSize: 12, marginBottom: 8 }}>
                            {detail.optionen.map(
                              (o: string, i: number) => <li key={i}>{o}</li>)}
                          </ul>
                        )}

                        <div style={{ margin: '8px 0',
                                      padding: '8px 10px',
                                      border: '1px solid var(--rand, #ddd)',
                                      borderRadius: 4 }}>
                          {detail.kiGeneriert ? (
                            <>
                              <div style={{ fontSize: 12, marginBottom: 6 }}>
                                {detail.kiKurzfassung}
                              </div>

                              {detail.kiEmpfehlung && (
                                <div style={{ fontSize: 12, fontWeight: 600,
                                              marginBottom: 6 }}>
                                  Empfehlung: {detail.kiEmpfehlung}
                                </div>
                              )}

                              {Array.isArray(detail.kiRisiken)
                                && detail.kiRisiken.length > 0 && (
                                <div style={{ fontSize: 12, marginBottom: 6 }}>
                                  <strong>Risiken</strong>
                                  <ul style={{ margin: '2px 0' }}>
                                    {detail.kiRisiken.map(
                                      (r: string, i: number) =>
                                        <li key={i}>{r}</li>)}
                                  </ul>
                                </div>
                              )}

                              {Array.isArray(detail.kiRueckfragen)
                                && detail.kiRueckfragen.length > 0 && (
                                <div style={{ fontSize: 12, marginBottom: 6 }}>
                                  <strong>Rückfragen</strong>
                                  <ul style={{ margin: '2px 0' }}>
                                    {detail.kiRueckfragen.map(
                                      (r: string, i: number) =>
                                        <li key={i}>{r}</li>)}
                                  </ul>
                                </div>
                              )}

                              <div className="schwach" style={{ fontSize: 11 }}>
                                KI-Erläuterung ({anbieterModell(detail.kiModell)}
                                {detail.kiErzeugtAm
                                  ? `, ${new Date(detail.kiErzeugtAm)
                                      .toLocaleDateString('de-DE')}`
                                  : ''}) — aus den Fakten der Plattform
                                erzeugt, keine eigene Messung.
                              </div>
                            </>
                          ) : (
                            <div className="schwach" style={{ fontSize: 12 }}>
                              Noch keine KI-Erläuterung erzeugt.
                            </div>
                          )}

                          <button
                              className="m-knopf"
                              style={{ marginTop: 6 }}
                              disabled={erlaeuternLaeuft}
                              onClick={onErlaeutern}>
                            {erlaeuternLaeuft
                              ? 'wird erzeugt …' : 'Erläuterung erzeugen'}
                          </button>
                        </div>

                        <textarea
                          placeholder="Entscheidung …"
                          value={entscheidungstext}
                          onChange={(e) => onEntscheidungstextChange(
                            e.target.value)}
                          style={{ width: '100%', minHeight: 60,
                                   fontSize: 12 }}
                        />

                        <div style={{ display: 'flex', gap: 8,
                                      marginTop: 8 }}>
                          <button
                            className="m-knopf m-knopf-haupt"
                            disabled={!entscheidungstext}
                            onClick={() => onEntscheiden('entschieden')}>
                            Entschieden
                          </button>
                          <button className="m-knopf"
                                  onClick={() => onEntscheiden('in_arbeit')}>
                            In Arbeit
                          </button>
                          <button className="m-knopf"
                                  onClick={() => onEntscheiden('verworfen')}>
                            Verwerfen
                          </button>
                        </div>
            </div>
          )}
        </div>
      )}
    </ContentSection>
  );
}

/**
 * Der Status einer Planeraufgabe als Musterzustand (Abschnitt 18).
 *
 * „offen" ist `warnung` und nicht `fehler`: Eine offene Aufgabe ist der
 * Normalfall und kein Schaden.
 */
function statuszustand(status: string): Zustand {
  switch (status) {
    case 'entschieden': return 'gut';
    case 'in_arbeit': return 'info';
    case 'verworfen': return 'neutral';
    default: return 'warnung';
  }
}
